import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import useCampaignQuoteItems from '@hooks/useCampaignQuoteItems'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignQuoteSectionActions from '@redux/modules/campaignQuoteSection'

import defaultFormState, { requiredFields } from '@models/campaignQuoteSection'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { CampaignQuoteSectionModel, CampaignQuoteSectionRequestOptions } from '@models/types'

export const generateUrls = (campaignQuoteSection?: Partial<CampaignQuoteSectionModel>) => {
  const { id } = campaignQuoteSection || {}

  return {
    editCampaignQuoteSectionDetailsUrl: `#/systemManager/campaignQuoteSections/${id}/edit/details`,
    editCampaignQuoteSectionUrl: `#/systemManager/campaignQuoteSections/${id}/edit/`,
    campaignQuoteSectionsIndexUrl: '#/systemManager/campaignQuoteSections',
  }
}

type CreateCampaignQuoteSectionParams = {
  campaignQuoteSectionParams: Partial<CampaignQuoteSectionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteSectionRequestOptions,
}

const createCampaignQuoteSection = (params: CreateCampaignQuoteSectionParams) => {
  const { dispatch, campaignQuoteSectionParams, requestOptions } = params
  const { createCampaignQuoteSection: createFn } = campaignQuoteSectionActions

  return dispatch(createFn(campaignQuoteSectionParams, requestOptions))
}

type UpdateCampaignQuoteSectionParams = {
  campaignQuoteSection: CampaignQuoteSectionModel,
  campaignQuoteSectionParams: Partial<CampaignQuoteSectionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteSectionRequestOptions,
}

const updateCampaignQuoteSection = (params: UpdateCampaignQuoteSectionParams) => {
  const {
    dispatch, campaignQuoteSection, campaignQuoteSectionParams, requestOptions,
  } = params
  const { updateCampaignQuoteSection: updateFn } = campaignQuoteSectionActions

  const updatedParams = {
    id: campaignQuoteSection.id,
    ...campaignQuoteSectionParams,
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type UpdateQuoteSectionOrderParams = {
  campaignQuoteId: number,
  dispatch: AppDispatch,
  requestOptions?: CampaignQuoteSectionRequestOptions,
  sortedQuoteSectionIds: number[],
}

const updateQuoteSectionOrder = (params: UpdateQuoteSectionOrderParams) => {
  const {
    campaignQuoteId, dispatch, requestOptions, sortedQuoteSectionIds,
  } = params
  const { updateQuoteSectionOrder: updateFn } = campaignQuoteSectionActions

  return dispatch(updateFn(campaignQuoteId, sortedQuoteSectionIds, requestOptions))
}

type MoveQuoteSectionIdParams = {
  campaignQuoteId: number,
  campaignQuoteSectionId: number,
  campaignQuoteSectionIds: number[],
  dispatch: AppDispatch,
  fromIndex: number,
  requestOptions?: CampaignQuoteSectionRequestOptions,
  toIndex: number,
}

const moveQuoteSectionId = (params: MoveQuoteSectionIdParams) => {
  const {
    campaignQuoteId,
    campaignQuoteSectionId,
    campaignQuoteSectionIds,
    dispatch,
    fromIndex,
    requestOptions,
    toIndex,
  } = params

  const updatedSectionIds = [...campaignQuoteSectionIds]

  // If we are creating a new section we need to add it to the array first
  // so we can sort it correctly
  const currentSectionIndex = updatedSectionIds.findIndex(id => id === campaignQuoteSectionId)
  if (currentSectionIndex === -1) updatedSectionIds.push(campaignQuoteSectionId)

  updatedSectionIds.splice(toIndex, 0, updatedSectionIds.splice(fromIndex, 1)[0])

  return updateQuoteSectionOrder({
    campaignQuoteId,
    dispatch,
    requestOptions,
    sortedQuoteSectionIds: updatedSectionIds,
  })
}

type AddSectionAtIndexParams = {
  campaignQuoteId: number,
  campaignQuoteSectionIds: number[],
  currentIndex: number,
  dispatch: AppDispatch,
  lastIndex: number,
  requestOptions?: CampaignQuoteSectionRequestOptions,
}

const addSectionAtIndex = async (params: AddSectionAtIndexParams) => {
  const {
    campaignQuoteId,
    campaignQuoteSectionIds,
    currentIndex,
    dispatch,
    lastIndex,
    requestOptions,
  } = params

  // Create Section
  const createResult = await createCampaignQuoteSection({
    campaignQuoteSectionParams: {
      campaign_quote_id: campaignQuoteId,
      title: 'Marketing',
    },
    dispatch,
    requestOptions,
  })

  // Return if failed
  if (!createResult.success) return createResult

  // Move in to position
  return moveQuoteSectionId({
    campaignQuoteId,
    // @ts-expect-error TS2322
    campaignQuoteSectionId: createResult.data.id,
    campaignQuoteSectionIds,
    dispatch,
    fromIndex: lastIndex + 1,
    requestOptions,
    toIndex: currentIndex + 1,
  })
}

type DeleteCampaignQuoteSectionParams = {
  // @ts-expect-error TS2344
  campaignQuoteSection: DeleteParams<CampaignQuoteSectionModel>,
  dispatch: AppDispatch,
}

const deleteCampaignQuoteSection = (params: DeleteCampaignQuoteSectionParams) => {
  const { dispatch, campaignQuoteSection } = params
  const { deleteCampaignQuoteSection: deleteFn } = campaignQuoteSectionActions

  return dispatch(deleteFn(campaignQuoteSection))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignQuoteSectionForm(
  campaignQuoteSection: Partial<CampaignQuoteSectionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignQuoteSectionForm = useForm(
    defaultFormState,
    { entity: campaignQuoteSection, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [campaignQuoteSection.id, campaignQuoteSection.cache_key],
  )

  return {
    ...campaignQuoteSectionForm,
  }
}

export const useRelations = (campaignQuoteSection: Partial<CampaignQuoteSectionModel> = {}) => {
  const { campaign_quote_id } = campaignQuoteSection

  const { campaignQuotes } = useSelector(reduxState => reduxState.entities)

  const campaignQuote = campaign_quote_id && campaignQuotes[campaign_quote_id]
    ? campaignQuotes[campaign_quote_id] : {}

  return {
    campaignQuote,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteSectionRequestOptions,
}

function useCampaignQuoteSection(initEntity: Partial<CampaignQuoteSectionModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  const { entity: campaignQuoteSection }: { entity: CampaignQuoteSectionModel } = useLatestEntity(
    initEntity,
    'campaignQuoteSections',
  )
  const { id } = campaignQuoteSection

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaignQuote } = useRelations(campaignQuoteSection)

  useReduxAction(
    'campaignQuoteSections',
    'loadCampaignQuoteSection',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignQuoteSections)

  // Items
  const campaignQuoteItemsPayload = useCampaignQuoteItems({
    filters: {
      campaign_quote_section_id: id,
    },
    performHttpRequests: false,
  })
  const { filteredCampaignQuoteItems } = campaignQuoteItemsPayload

  const subtotal = filteredCampaignQuoteItems
    .filter((item => item.selected))
    .reduce((acc, item) => acc + parseFloat(digObject(item, 'price_override', 0)), 0)

  const hideDates = digObject(campaignQuoteSection, 'options.hide_dates', false)
  const hidePrices = digObject(campaignQuoteSection, 'options.hide_prices', false)
  const showSubtotal = digObject(campaignQuoteSection, 'options.show_subtotal', true)

  return {
    callbacks: {
      addCustomItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteItemModal',
        payload: {
          campaignQuoteItem: {
            // @ts-expect-error TS2339
            campaign_quote_id: campaignQuote.id,
            campaign_quote_section_id: campaignQuoteSection.id,
            item_type: 'custom',
            price: '0',
          },
        },
      }),
      addOrderableItem: () => launchModal({
        callbacks,
        modalKey: 'NewOrderModal',
        payload: {
          // @ts-expect-error TS2339
          agencyId: campaignQuote.agency_id,
          // @ts-expect-error TS2339
          campaignId: campaignQuote.campaign_id,
          // @ts-expect-error TS2339
          campaignQuoteId: campaignQuote.id,
          quoteSectionId: campaignQuoteSection.id,
        },
      }),
      addSectionAtIndex: (
        params: { campaignQuoteSectionIds: number[], index: number, lastIndex: number },
        entityOptions?: CampaignQuoteSectionRequestOptions,
      ) => {
        const { campaignQuoteSectionIds, index, lastIndex } = params

        return addSectionAtIndex({
          // @ts-expect-error TS2339
          campaignQuoteId: campaignQuote.id,
          campaignQuoteSectionIds,
          currentIndex: index,
          dispatch,
          lastIndex,
          requestOptions: entityOptions,
        })
      },
      createCampaignQuoteSection: (
        campaignQuoteSectionParams: Partial<CampaignQuoteSectionModel>,
        entityOptions?: CampaignQuoteSectionRequestOptions,
      ) => (
        createCampaignQuoteSection({ campaignQuoteSectionParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCampaignQuoteSection: (customPayload?: object) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignQuoteSectionModal',
        payload: { ...customPayload, campaignQuoteSection },
      }),
      // @ts-expect-error TS2322
      deleteCampaignQuoteSection: () => deleteCampaignQuoteSection({ dispatch, campaignQuoteSection }),
      moveQuoteSectionId: (
        params: { campaignQuoteSectionIds: number[], fromIndex: number, toIndex: number },
        entityOptions?: CampaignQuoteSectionRequestOptions,
      ) => {
        const { campaignQuoteSectionIds, fromIndex, toIndex } = params

        return moveQuoteSectionId({
          // @ts-expect-error TS2339
          campaignQuoteId: campaignQuote.id,
          // @ts-expect-error TS2322
          campaignQuoteSectionId: campaignQuoteSection.id,
          campaignQuoteSectionIds,
          dispatch,
          fromIndex,
          requestOptions: entityOptions,
          toIndex,
        })
      },
      updateCampaignQuoteSection: (
        campaignQuoteSectionParams: Partial<CampaignQuoteSectionModel>,
        entityOptions?: CampaignQuoteSectionRequestOptions,
      ) => (
        updateCampaignQuoteSection({
          campaignQuoteSection, campaignQuoteSectionParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateQuoteSectionOrder: (
        params: { campaignQuoteId: number, sortedQuoteSectionIds: number[] },
        entityOptions?: CampaignQuoteSectionRequestOptions,
      ) => {
        const { campaignQuoteId, sortedQuoteSectionIds } = params

        return updateQuoteSectionOrder({
          campaignQuoteId,
          sortedQuoteSectionIds,
          dispatch,
          requestOptions: entityOptions,
        })
      },
    },
    campaignQuote,
    campaignQuoteSection,
    creating,
    deleting,
    hideDates,
    hidePrices,
    // @ts-expect-error TS2345
    loading: loading && !loadedIds.includes(campaignQuoteSection.id),
    showSubtotal,
    subtotal,
    updating,
    urls: generateUrls(campaignQuoteSection),
  }
}

export default useCampaignQuoteSection
